import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Divider,
  Alert,
  AlertTitle,
  IconButton,
  CircularProgress,
  Collapse,
  Tabs,
  Tab,
  Paper,
  Autocomplete,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  TaskAlt as TaskIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  Add as AddIcon,
  Today as TodayIcon,
  DateRange as WeekIcon,
  CalendarMonth as MonthIcon,
  CalendarToday as YearIcon,
  Business as BusinessIcon,
  AccessTime as AccessTimeIcon,
  PhotoCamera as PhotoCameraIcon,
  Cancel as CancelIcon,
  Block as BlockIcon,
  QrCode as QrCodeIcon,
} from '@mui/icons-material';
import TaskGroupsModal from './TaskGroupsModal';
import { storage } from '../../../firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getBranches as getBranchesAdapter } from '../../../services/migration/branchDataService';
import { taskDataService } from '../../../services/taskDataService';
import NotificationService from '../../../services/NotificationService';

interface AddTaskModalProps {
  open: boolean;
  onClose: () => void;
  onAddTask: (taskData: {
    name: string;
    description: string;
    personnelId: string;
    completionType: string;
    isRecurring: boolean;
    repeatType: string;
    dailyRepetitions?: number;
    startTolerance: number;
    repetitionTimes?: string[];
    groupId?: string;
    weekDays?: number[];
    monthDay?: number;
    yearDate?: string;
    branchesId?: string;
    monthlyData?: {
      month: number;
      monthName: string;
      days: {
        day: number;
        dailyRepetitions: number;
        repetitionTimes: string[];
      }[];
    }[];
    yearlyPlanDetails?: string;
    deadline?: string;
    startDate?: string;
    requestCompletionPhoto?: boolean;
    canBeRejected?: boolean;
    imageUrls?: string[];
  }) => Promise<void>;
  onAddWeeklyTask?: (taskData: {
    name: string;
    description: string;
    personnelId: string;
    completionType: string;
    weekDays: {
      day: number;
      dailyRepetitions: number;
      repetitionTimes: string[];
    }[];
    startTolerance: number;
    groupId?: string;
    branchesId?: string;
  }) => Promise<void>;
  personnel: any[];
  taskGroups?: any[];
  companyId?: string;
  branchId?: string;
  isManager?: boolean;
  title?: string;
  isFromSurveys?: boolean;
}

const AddTaskModal: React.FC<AddTaskModalProps> = ({ 
  open, 
  onClose, 
  onAddTask, 
  onAddWeeklyTask,
  personnel, 
  taskGroups = [], 
  companyId = '',
  branchId,
  isManager = false,
  title = "Yeni Görev Ekle",
  isFromSurveys = false
}) => {
  const [taskName, setTaskName] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [selectedPersonnelId, setSelectedPersonnelId] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [isNormalTask, setIsNormalTask] = useState(true);
  const [completionType, setCompletionType] = useState('button');
  const [repeatType, setRepeatType] = useState('daily');
  const [dailyRepetitions, setDailyRepetitions] = useState(1);
  const [startTolerance, setStartTolerance] = useState(15);
  const [repetitionTimes, setRepetitionTimes] = useState<string[]>(['12:00']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taskGroupsModalOpen, setTaskGroupsModalOpen] = useState(false);
  const [deadline, setDeadline] = useState<string>('');
  const [deadlineTime, setDeadlineTime] = useState<string>('18:00');
  const [startDate, setStartDate] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('09:00');
  const [requestCompletionPhoto, setRequestCompletionPhoto] = useState(false);
  const [canBeRejected, setCanBeRejected] = useState(false);
  
  // Şube filtresi için yeni state'ler
  const [branches, setBranches] = useState<Array<{id: string, name: string}>>([]);
  const [selectedBranch, setSelectedBranch] = useState<{id: string, name: string} | null>(null);
  const [filteredPersonnel, setFilteredPersonnel] = useState<any[]>([]);
  
  // Haftalık, aylık ve yıllık tekrar seçenekleri için state'ler
  const [weekDays, setWeekDays] = useState<number[]>([1]); // Pazartesi varsayılan
  const [monthDay, setMonthDay] = useState<number>(1); // Ayın ilk günü varsayılan
  const [yearDate, setYearDate] = useState<string>('01-01'); // 1 Ocak varsayılan
  const [yearlyPlanDetails, setYearlyPlanDetails] = useState<string>(''); // Yıllık görev detayları
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear()); // Yıl seçimi için

  // Haftalık görevler için yeni state'ler
  const [weeklyTaskDays, setWeeklyTaskDays] = useState<{
    day: number;
    dayName: string;
    selected: boolean;
    dailyRepetitions: number;
    repetitionTimes: string[];
  }[]>([
    { day: 1, dayName: 'Pazartesi', selected: false, dailyRepetitions: 1, repetitionTimes: ['12:00'] },
    { day: 2, dayName: 'Salı', selected: false, dailyRepetitions: 1, repetitionTimes: ['12:00'] },
    { day: 3, dayName: 'Çarşamba', selected: false, dailyRepetitions: 1, repetitionTimes: ['12:00'] },
    { day: 4, dayName: 'Perşembe', selected: false, dailyRepetitions: 1, repetitionTimes: ['12:00'] },
    { day: 5, dayName: 'Cuma', selected: false, dailyRepetitions: 1, repetitionTimes: ['12:00'] },
    { day: 6, dayName: 'Cumartesi', selected: false, dailyRepetitions: 1, repetitionTimes: ['12:00'] },
    { day: 0, dayName: 'Pazar', selected: false, dailyRepetitions: 1, repetitionTimes: ['12:00'] },
  ]);

  // Görev grupları için filtreleme state'i ekleyelim
  const [filteredTaskGroups, setFilteredTaskGroups] = useState<any[]>([]);

  // Aylık görevler için yeni state'ler
  const [selectedMonths, setSelectedMonths] = useState<number[]>([]);
  const [monthlyTaskDays, setMonthlyTaskDays] = useState<{
    month: number;
    monthName: string;
    days: number[];
    dailyRepetitions: number;
    repetitionTimes: string[];
  }[]>([]);

  // Aylar listesi
  const months = [
    { value: 0, label: 'Ocak' },
    { value: 1, label: 'Şubat' },
    { value: 2, label: 'Mart' },
    { value: 3, label: 'Nisan' },
    { value: 4, label: 'Mayıs' },
    { value: 5, label: 'Haziran' },
    { value: 6, label: 'Temmuz' },
    { value: 7, label: 'Ağustos' },
    { value: 8, label: 'Eylül' },
    { value: 9, label: 'Ekim' },
    { value: 10, label: 'Kasım' },
    { value: 11, label: 'Aralık' }
  ];

  // Resim ekleme için state'ler
  const [taskImages, setTaskImages] = useState<Array<{
    file: File | null,
    preview: string,
    uploading: boolean,
    error: string
  }>>([
    { file: null, preview: '', uploading: false, error: '' },
    { file: null, preview: '', uploading: false, error: '' },
    { file: null, preview: '', uploading: false, error: '' },
    { file: null, preview: '', uploading: false, error: '' },
    { file: null, preview: '', uploading: false, error: '' }
  ]);

  // Şube adını getir - branchesId'den şube adını bulmak için yardımcı fonksiyon
  const getBranchName = (branchId: string | null | object): string => {
    if (!branchId) return '';
    
    // Eğer branchId bir object ise, ilk anahtarı al
    let branchIdStr: string;
    if (typeof branchId === 'object' && branchId !== null) {
      const keys = Object.keys(branchId);
      if (keys.length > 0) {
        branchIdStr = keys[0];
      } else {
        return '';
      }
    } else {
      branchIdStr = branchId as string;
    }
    
    // branches dizisini kullanarak şube adını bul
    const branch = branches.find(b => b.id === branchIdStr);
    return branch ? branch.name : '';
  };

  // Şubeleri yükle
  useEffect(() => {
    const loadBranches = async () => {
      if (!companyId) return;
      
      try {
        // Subeler SQL-once (branchDataService); id = firebaseId.
        const serviceBranches = await getBranchesAdapter(companyId);
        const branchesList = serviceBranches.map((b) => ({ id: b.id, name: b.name || 'İsimsiz Şube' }));
        setBranches(branchesList);
        console.log('Yüklenen şubeler:', branchesList);
      } catch (error) {
        console.error('Şube verileri yüklenirken hata:', error);
      }
    };
    
    if (open) {
      loadBranches();
    }
  }, [companyId, open]);

  // Faz 5 DILIM 1: gorev gruplari SQL'den (taskDataService); id = legacyFirebaseKey.
  const loadTaskGroups = async () => {
    if (!companyId) return;

    try {
      // RTDB-uyumlu sekil: branchesId tek string'e normalize edilmis gelir.
      const allGroups = await taskDataService.listGroupsLegacy();

      if (isManager && branchId) {
        // Şube müdürü için filtreleme - SADECE kendi şubesine ait grupları göster
        // (genel/branchesId'siz gruplar gösterilmez).
        const managerGroups = allGroups.filter(
          (group) => !!group.branchesId && group.branchesId === branchId
        );
        setFilteredTaskGroups(managerGroups);
      } else {
        setFilteredTaskGroups(allGroups);
      }
    } catch (error) {
      console.error('Görev grupları yüklenirken hata:', error);
      setFilteredTaskGroups([]);
    }
  };

  // Form açıldığında başlangıç değerlerini yükle
  useEffect(() => {
    if (open) {
      // Başlangıçta filtrelenmiş personel listesini tüm personel olarak ayarla
      setFilteredPersonnel(personnel);
      
      // Görev gruplarını doğrudan Firebase'den yükle
      loadTaskGroups();
      
      // Surveys.tsx'ten açıldıysa tekrarlı görevleri seçili hale getir
      if (isFromSurveys) {
        setIsRecurring(true);
        setIsNormalTask(false);
        setCompletionType('qr');
        console.log('Surveys sayfasından açıldı, tekrarlı görev ve QR kod tamamlama seçili hale getirildi');
      }
      
      // Şube müdürü ise ve branchId varsa, şubeyi otomatik olarak seç
      if (isManager && branchId) {
        const managerBranch = branches.find(branch => branch.id === branchId);
        if (managerBranch) {
          setSelectedBranch(managerBranch);
          console.log(`Şube müdürü için otomatik şube seçildi: ${managerBranch.name} (${managerBranch.id})`);
        } else {
          // Eğer branches yüklenmemişse, branches yüklendiğinde seçilmesi için state'i güncelle
          console.log(`Şube müdürü için şube bulunamadı, branches yüklendiğinde tekrar denenecek: ${branchId}`);
        }
      }
      
      // Personelin branchesId değerlerini kontrol et (farklı formatlardaki branchesId'leri hesaba kat)
      const personnelWithBranchCount = personnel.filter(p => {
        if (!p.branchesId) return false;
        
        if (typeof p.branchesId === 'object') {
          return Object.keys(p.branchesId).length > 0;
        }
        
        return true;
      }).length;
      
      console.log(`Modal açıldı, toplam personel: ${personnel.length}, şube bilgisi olan: ${personnelWithBranchCount}`);
      
      // Şube bilgisi olan personeli kontrol et
      if (personnelWithBranchCount === 0 && personnel.length > 0) {
        console.warn('Hiçbir personelin şube bilgisi (branchesId) bulunamadı!');
      }
    }
  }, [open, personnel, isManager, branchId, branches, companyId, isFromSurveys]);

  // Şube değiştiğinde hem personel hem de görev gruplarını filtrele
  useEffect(() => {
    if (!selectedBranch) {
      // Şube seçili değilse tüm personeli göster
      setFilteredPersonnel(personnel);
      
      // Şube müdürü değilse veya branchId yoksa, görev gruplarını yeniden yükle
      if (!isManager || !branchId) {
        loadTaskGroups();
      }
      return;
    }
    
    console.log("Şube ID:", selectedBranch.id);
    console.log("Toplam personel sayısı:", personnel.length);
    
    // Personel filtreleme - Şube ID'sine göre personel filtreleme
    const filteredPerson = personnel.filter(person => {
      // branchesId'nin farklı formatları için kontrol
      let personBranchId = person.branchesId;
      
      console.log(`Personel: ${person.name || person.id}, branchesId: ${JSON.stringify(personBranchId)}`);
      
      // Eğer branchesId bir object ise, ilk anahtarı al
      if (typeof personBranchId === 'object' && personBranchId !== null) {
        const keys = Object.keys(personBranchId);
        if (keys.length > 0) {
          personBranchId = keys[0];
          console.log(`  - Object branchesId için ilk anahtar: ${personBranchId}`);
        }
      }
      
      // Null veya undefined kontrolü
      if (!personBranchId) {
        console.log(`  - branchesId yok, atlanıyor`);
        return false;
      }
      
      // String karşılaştırması yaparak eşleşmeyi kontrol et
      const isMatch = String(personBranchId) === String(selectedBranch.id);
      console.log(`  - Karşılaştırma: "${personBranchId}" === "${selectedBranch.id}" = ${isMatch}`);
      return isMatch;
    });
    
    console.log(`Şube "${selectedBranch.name}" için bulunan personel sayısı: ${filteredPerson.length}`);
    
    if (filteredPerson.length === 0) {
      console.warn(`Uyarı: "${selectedBranch.name}" şubesinde hiç personel bulunamadı.`);
    }
    
    setFilteredPersonnel(filteredPerson);
    
    // Eğer şu anki seçili personel bu şubede değilse, seçimi temizle
    if (selectedPersonnelId) {
      const personelExists = filteredPerson.some(p => p.id === selectedPersonnelId);
      if (!personelExists) {
        setSelectedPersonnelId('');
      }
    }
    
    // Şube değiştiğinde görev gruplarını yeniden yükle
    loadTaskGroups();
    
  }, [selectedBranch, personnel, selectedPersonnelId]);

  // Bugünün tarihini YYYY-MM-DD formatında al (bitiş tarihi için minimum değer)
  const today = new Date().toISOString().split('T')[0];

  // Form alanlarını sıfırla
  const resetForm = () => {
    setTaskName('');
    setTaskDescription('');
    setSelectedPersonnelId('');
    setSelectedBranch(null);
    setSelectedGroupId('');
    
    // Surveys.tsx'ten açıldıysa tekrarlı görev seçili kalsın, değilse normal başlangıç
    if (isFromSurveys) {
      setIsRecurring(true);
      setIsNormalTask(false);
      setCompletionType('qr');
    } else {
      setIsRecurring(false);
      setIsNormalTask(true);
      setCompletionType('button');
    }
    
    setRepeatType('daily');
    setDailyRepetitions(1);
    setStartTolerance(15);
    setRepetitionTimes(['12:00']);
    setYearlyPlanDetails('');
    setSelectedYear(new Date().getFullYear());
    setError(null);
    setDeadline('');
    setDeadlineTime('18:00');
    setStartDate('');
    setStartTime('09:00');
    setRequestCompletionPhoto(false);
    setCanBeRejected(false);
    
    // Resim yükleme alanını sıfırla
    setTaskImages([
      { file: null, preview: '', uploading: false, error: '' },
      { file: null, preview: '', uploading: false, error: '' },
      { file: null, preview: '', uploading: false, error: '' },
      { file: null, preview: '', uploading: false, error: '' },
      { file: null, preview: '', uploading: false, error: '' }
    ]);
    
    // Haftalık görev günlerini sıfırla
    setWeeklyTaskDays(weeklyTaskDays.map(day => ({
      ...day,
      selected: false,
      dailyRepetitions: 1,
      repetitionTimes: ['12:00']
    })));
  };

  // Modal kapandığında formu sıfırla
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open, isFromSurveys]);

  // Tekrar sayısı değiştiğinde repetitionTimes dizisini güncelle
  useEffect(() => {
    if (dailyRepetitions > repetitionTimes.length) {
      // Yeni saat ekle
      const newTimes = [...repetitionTimes];
      for (let i = repetitionTimes.length; i < dailyRepetitions; i++) {
        newTimes.push('12:00');
      }
      setRepetitionTimes(newTimes);
    } else if (dailyRepetitions < repetitionTimes.length) {
      // Fazla saatleri kaldır
      setRepetitionTimes(repetitionTimes.slice(0, dailyRepetitions));
    }
  }, [dailyRepetitions]);

  // Tekrar saatini güncelle
  const handleTimeChange = (index: number, newTime: string) => {
    const newTimes = [...repetitionTimes];
    newTimes[index] = newTime;
    setRepetitionTimes(newTimes);
  };

  // Haftalık tekrar için gün seçimini güncelle
  const handleWeekDayChange = (day: number) => {
    if (weekDays.includes(day)) {
      // Eğer zaten seçili ise kaldır (en az bir gün seçili olmalı)
      if (weekDays.length > 1) {
        setWeekDays(weekDays.filter(d => d !== day));
      }
    } else {
      // Seçili değilse ekle
      setWeekDays([...weekDays, day]);
    }
  };

  // Haftalık görev günü seçimi
  const handleWeeklyDayToggle = (dayIndex: number) => {
    const updatedDays = [...weeklyTaskDays];
    updatedDays[dayIndex].selected = !updatedDays[dayIndex].selected;
    setWeeklyTaskDays(updatedDays);
  };
  
  // Haftalık görev günlük tekrar sayısı değişimi
  const handleWeeklyDayRepetitionChange = (dayIndex: number, repetitions: number) => {
    const updatedDays = [...weeklyTaskDays];
    updatedDays[dayIndex].dailyRepetitions = repetitions;
    
    // Tekrar sayısına göre saatleri güncelle
    const currentTimes = updatedDays[dayIndex].repetitionTimes;
    if (repetitions > currentTimes.length) {
      // Yeni saat ekle
      const newTimes = [...currentTimes];
      for (let i = currentTimes.length; i < repetitions; i++) {
        newTimes.push('12:00');
      }
      updatedDays[dayIndex].repetitionTimes = newTimes;
    } else if (repetitions < currentTimes.length) {
      // Fazla saatleri kaldır
      updatedDays[dayIndex].repetitionTimes = currentTimes.slice(0, repetitions);
    }
    
    setWeeklyTaskDays(updatedDays);
  };
  
  // Haftalık görev gün saati değişimi
  const handleWeeklyDayTimeChange = (dayIndex: number, timeIndex: number, newTime: string) => {
    const updatedDays = [...weeklyTaskDays];
    updatedDays[dayIndex].repetitionTimes[timeIndex] = newTime;
    setWeeklyTaskDays(updatedDays);
  };

  // Ay seçimi değiştiğinde
  const handleMonthChange = (monthValue: number) => {
    if (selectedMonths.includes(monthValue)) {
      // Ay seçimi kaldırıldıysa
      setSelectedMonths(selectedMonths.filter(m => m !== monthValue));
      setMonthlyTaskDays(monthlyTaskDays.filter(m => m.month !== monthValue));
    } else {
      // Yeni ay eklendiyse
      setSelectedMonths([...selectedMonths, monthValue]);
      const monthName = months.find(m => m.value === monthValue)?.label || '';
      setMonthlyTaskDays([
        ...monthlyTaskDays,
        {
          month: monthValue,
          monthName,
          days: [],
          dailyRepetitions: 1,
          repetitionTimes: ['12:00']
        }
      ]);
    }
  };

  // Ayın günleri değiştiğinde
  const handleMonthDayChange = (monthValue: number, day: number) => {
    setMonthlyTaskDays(monthlyTaskDays.map(monthData => {
      if (monthData.month === monthValue) {
        if (monthData.days.includes(day)) {
          // Gün seçimi kaldırıldıysa
          return {
            ...monthData,
            days: monthData.days.filter(d => d !== day)
          };
        } else {
          // Yeni gün eklendiyse
          return {
            ...monthData,
            days: [...monthData.days, day]
          };
        }
      }
      return monthData;
    }));
  };

  // Ayın günlük tekrar sayısı değiştiğinde
  const handleMonthDayRepetitionChange = (monthValue: number, repetitions: number) => {
    setMonthlyTaskDays(monthlyTaskDays.map(monthData => {
      if (monthData.month === monthValue) {
        const newTimes = [...monthData.repetitionTimes];
        if (repetitions > newTimes.length) {
          // Yeni saat ekle
          for (let i = newTimes.length; i < repetitions; i++) {
            newTimes.push('12:00');
          }
        } else if (repetitions < newTimes.length) {
          // Fazla saatleri kaldır
          newTimes.length = repetitions;
        }
        return {
          ...monthData,
          dailyRepetitions: repetitions,
          repetitionTimes: newTimes
        };
      }
      return monthData;
    }));
  };

  // Ayın gün saati değiştiğinde
  const handleMonthDayTimeChange = (monthValue: number, timeIndex: number, newTime: string) => {
    setMonthlyTaskDays(monthlyTaskDays.map(monthData => {
      if (monthData.month === monthValue) {
        const newTimes = [...monthData.repetitionTimes];
        newTimes[timeIndex] = newTime;
        return {
          ...monthData,
          repetitionTimes: newTimes
        };
      }
      return monthData;
    }));
  };

  // Normal görev seçildiğinde, tekrarlı görevi kapat
  const handleNormalTaskChange = (checked: boolean) => {
    setIsNormalTask(checked);
    console.log("Normal görev seçildi:", checked);
    console.log("canBeRejected state değeri:", canBeRejected);
    if (checked) {
      setIsRecurring(false);
      setCompletionType('button');
      setDeadline('');
      setDeadlineTime('18:00');
      setStartDate('');
      setStartTime('09:00');
    } else {
      // Normal görev kapatıldığında completion type'ı sıfırlama, kullanıcı seçsin
      setRequestCompletionPhoto(false);
      setCanBeRejected(false);
    }
  };

  // Tekrarlı görev seçildiğinde, normal görevi kapat
  const handleRecurringTaskChange = (checked: boolean) => {
    setIsRecurring(checked);
    if (checked) {
      setIsNormalTask(false);
      // Tekrarlı görevlerde artık otomatik olarak QR seçmeyelim, kullanıcı seçsin
      // setCompletionType('qr'); // Bu satırı kaldırıyoruz
      setDeadline('');
      setDeadlineTime('18:00');
      setStartDate('');
      setStartTime('09:00');
      setRequestCompletionPhoto(false);
      setCanBeRejected(false);
    } else {
      setCompletionType('button');
    }
  };

  // Görev ekle
  const handleSubmit = async () => {
    // Validation
    if (!taskName.trim()) {
      setError('Lütfen bir görev adı girin.');
      return;
    }

    if (!taskDescription.trim()) {
      setError('Lütfen bir görev açıklaması girin.');
      return;
    }

    if (!selectedPersonnelId) {
      setError('Lütfen bir personel seçin.');
      return;
    }
    
    if (!selectedGroupId) {
      setError('Lütfen bir görev grubu seçin.');
      return;
    }
    
    // Resim doğrulama (eğer normal görev ise ve resim yüklenecekse)
    if (isNormalTask) {
      const hasValidImages = taskImages.some(img => img.file !== null);
      const hasErrors = taskImages.some(img => img.error !== '');
      
      if (hasErrors) {
        setError('Lütfen hatalı resimleri düzeltin veya kaldırın.');
        return;
      }
      
      // Burada daha sonra resim yükleme işlemleri yapılacak
      // Şimdilik sadece doğrulama yapıyoruz
      console.log('Yüklenecek resimler:', taskImages.filter(img => img.file !== null).length);
    }
    
    // Aylık görev için en az bir ay seçili olmalı
    if (isRecurring && repeatType === 'monthly') {
      if (selectedMonths.length === 0) {
        setError('Lütfen en az bir ay seçin.');
        return;
      }
      
      // Her seçili ay için en az bir gün seçili olmalı
      for (const monthData of monthlyTaskDays) {
        if (monthData.days.length === 0) {
          setError(`Lütfen ${monthData.monthName} ayı için en az bir gün seçin.`);
          return;
        }
      }
    }
    
    // Yıllık görev için plan detayları kontrolü
    if (isRecurring && repeatType === 'yearly' && !yearlyPlanDetails.trim()) {
      setError('Lütfen yıllık görev için plan detaylarını girin.');
      return;
    }
    
    // Normal görev için tarih doğrulaması
    if (isNormalTask) {
      // Başlangıç tarihi varsa ve bugünden önceyse hata ver
      if (startDate && startDate < today) {
        setError('Başlangıç tarihi bugünden önce olamaz.');
        return;
      }
      
      // Bitiş tarihi varsa ve başlangıç tarihinden önceyse hata ver
      if (deadline && startDate && deadline < startDate) {
        setError('Bitiş tarihi başlangıç tarihinden önce olamaz.');
        return;
      }
      
      // Aynı gün ise, bitiş saati başlangıç saatinden sonra olmalı
      if (deadline && startDate && deadline === startDate && deadlineTime <= startTime) {
        setError('Aynı gün içinde bitiş saati başlangıç saatinden sonra olmalıdır.');
        return;
      }
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const branchesId: string | null = (isManager && branchId) ? branchId : (selectedBranch ? selectedBranch.id : null);
      let localTaskImageUrls: string[] = []; // handleSubmit içinde lokal olarak tanımlandı

      if (isNormalTask && companyId) {
        const imagesToUpload = taskImages.filter(img => img.file !== null);

        // Faz 5 DILIM 3: normal görev SQL'e gidiyor; görsel klasörü için geçici benzersiz id.
        const newTaskId = `new_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

        if (imagesToUpload.length > 0) {
          console.log(`${imagesToUpload.length} resim Firebase Storage'a yüklenecek... ID: ${newTaskId}`);
          // setTaskImages ile uploading: true ayarla
          const newImagesStateBeforeUpload = taskImages.map(imgState => {
            if (imagesToUpload.some(imgToUpload => imgToUpload.preview === imgState.preview && imgToUpload.file)) {
              return { ...imgState, uploading: true, error: '' };
            }
            return imgState;
          });
          setTaskImages(newImagesStateBeforeUpload);

          const uploadPromises = imagesToUpload.map((imageData) => {
            if (!imageData.file) return Promise.resolve(null);
            const imageRef = storageRef(storage, `companies/${companyId}/task-images/${newTaskId}/${imageData.file.name}`); // Dosya adını kullanabiliriz
            return uploadBytes(imageRef, imageData.file)
              .then(uploadResult => getDownloadURL(uploadResult.ref))
              .catch(err => {
                console.error('Resim yükleme hatası:', err, imageData.file?.name);
                // Hatalı resmi işaretle
                const newImagesStateAfterError = [...taskImages]; // En güncel taskImages state'ini al
                const errorImgIndex = newImagesStateAfterError.findIndex(img => img.preview === imageData.preview);
                if (errorImgIndex !== -1) {
                  newImagesStateAfterError[errorImgIndex].uploading = false;
                  newImagesStateAfterError[errorImgIndex].error = 'Yükleme hatası';
                  setTaskImages(newImagesStateAfterError);
                }
                return null; // Hata durumunda null dön, Promise.all devam etsin
              });
          });

          const downloadedUrls = await Promise.all(uploadPromises);
          localTaskImageUrls = downloadedUrls.filter(url => url !== null) as string[];

          // setTaskImages ile uploading: false ve hataları ayarla (Promise.all sonrası)
          const finalImagesState = taskImages.map(imgState => {
            const uploadedImage = imagesToUpload.find(upImg => upImg.preview === imgState.preview);
            if (uploadedImage) {
              const wasUploadedSuccessfully = localTaskImageUrls.some(url => url.includes(uploadedImage.file!.name));
              return {
                ...imgState,
                uploading: false,
                error: wasUploadedSuccessfully ? '' : (imgState.error || 'Yüklenemedi')
              };
            }
            return imgState;
          });
          setTaskImages(finalImagesState);
          
          if (imagesToUpload.length > 0 && localTaskImageUrls.length === 0 && !taskImages.some(img => img.error)) {
             // Resim yüklenmeye çalışıldı ama URL alınamadı ve explicit bir hata da set edilmedi.
             // Bu durum, Promise.all içindeki bir hatanın yakalanmaması veya null dönmesiyle oluşabilir.
             // Genel bir hata mesajı verilebilir.
             console.warn('Resimler yüklenmeye çalışıldı ancak URL alınamadı.');
          }
        }

        // Normal görev verisi (SQL'e onAddTask üzerinden gider)
        const normalTaskData: any = {
          name: taskName,
          description: taskDescription,
          personnelId: selectedPersonnelId,
          completionType: 'button',
          isRecurring: false,
          repeatType: 'none',
          startTolerance: 0,
          requestCompletionPhoto: requestCompletionPhoto,
          canBeRejected: canBeRejected,
        };

        if (selectedGroupId) normalTaskData.groupId = selectedGroupId;
        if (branchesId !== null) normalTaskData.branchesId = branchesId;
        if (deadline) normalTaskData.deadline = `${deadline}T${deadlineTime}`;
        if (startDate) normalTaskData.startDate = `${startDate}T${startTime}`;
        if (localTaskImageUrls.length > 0) {
          normalTaskData.imageUrls = localTaskImageUrls;
        }

        await onAddTask(normalTaskData);
        console.log('Normal görev SQL\'e kaydedildi.');

        // Bildirim gönderme — .NET notification API (tek çağrı: kaydet + FCM gönder)
        try {
          await NotificationService.sendNotification(companyId, [selectedPersonnelId], 'Yeni görevin var', taskName);
          console.log('Bildirim başarıyla gönderildi');
        } catch (notifError) {
          console.error('Bildirim gönderilirken hata:', notifError);
        }

        onClose();
        return; // Normal görev işlendikten sonra buradan çık
      }

      // Tekrarlı görevler için mantık (requestCompletionPhoto: false ile)
      if (isRecurring && repeatType === 'monthly') {
        const monthlyData = monthlyTaskDays.map(monthData => ({
          month: monthData.month,
          monthName: monthData.monthName,
          days: monthData.days.map(day => ({
            day,
            dailyRepetitions: monthData.dailyRepetitions,
            repetitionTimes: monthData.repetitionTimes
          }))
        }));
        const taskDataForAPI: any = {
          name: taskName, description: taskDescription, personnelId: selectedPersonnelId,
          completionType: completionType, isRecurring: true, repeatType, startTolerance, monthlyData,
          requestCompletionPhoto: false,
          canBeRejected: false,
        };
        if (selectedGroupId) taskDataForAPI.groupId = selectedGroupId;
        if (branchesId !== null) taskDataForAPI.branchesId = branchesId;
        await onAddTask(taskDataForAPI);
      } else if (isRecurring && repeatType === 'weekly' && onAddWeeklyTask) {
        const selectedDays = weeklyTaskDays
          .filter(day => day.selected)
          .map(({ day, dailyRepetitions, repetitionTimes }) => ({ day, dailyRepetitions, repetitionTimes }));
        const weeklyTaskData: any = {
          name: taskName, description: taskDescription, personnelId: selectedPersonnelId,
          completionType: completionType, weekDays: selectedDays, startTolerance,
          // requestCompletionPhoto: false, // onAddWeeklyTask tipi güncellenmediği için şimdilik yok
        };
        if (selectedGroupId) weeklyTaskData.groupId = selectedGroupId;
        if (branchesId !== null) weeklyTaskData.branchesId = branchesId;
        await onAddWeeklyTask(weeklyTaskData);
      } else if (isRecurring && repeatType === 'yearly') {
        const personel = personnel.find(p => p.id === selectedPersonnelId);
        // filteredTaskGroups SQL'den geliyor: id = legacyFirebaseKey, sqlId = SQL int.
        const group = filteredTaskGroups.find(g => g.id === selectedGroupId);
        // Faz 5 DILIM 2: yillik plan SQL'e (taskDataService.createYearlyPlan).
        await taskDataService.createYearlyPlan({
          name: taskName,
          description: taskDescription,
          personnelFirebaseUid: selectedPersonnelId || null,
          personnelName: personel?.name || null,
          groupId: group?.sqlId ?? null,
          groupName: group?.name || null,
          planDate: `${selectedYear}-${yearDate}`,
          yearlyPlanDetails: yearlyPlanDetails,
          branchesId: branchesId,
          branchName: branchesId !== null ? getBranchName(branchesId) : null,
          status: 'pending',
        });
        console.log('Yıllık görev planı SQL\'e kaydedildi');
      } else if (isRecurring && repeatType === 'daily') {
        const taskDataForAPI: any = {
          name: taskName, description: taskDescription, personnelId: selectedPersonnelId,
          completionType: completionType, isRecurring: true, repeatType, dailyRepetitions, startTolerance, repetitionTimes,
          requestCompletionPhoto: false,
          canBeRejected: false,
        };
        if (selectedGroupId) taskDataForAPI.groupId = selectedGroupId;
        if (branchesId !== null) taskDataForAPI.branchesId = branchesId;
        await onAddTask(taskDataForAPI);
      }

      // Genel bildirim (eğer yukarıdaki bir return ile çıkılmadıysa, yani tekrarlı görevse)
      // Ancak bildirim zaten normal görev bloğunda da var.
      // Bu yüzden bu genel bildirim ya kaldırılmalı ya da sadece tekrarlı görevler için çalışacak şekilde düzenlenmeli.
      // Şimdilik bırakıyorum, mevcut davranış korunmuş oluyor.
      try {
        await NotificationService.sendNotification(companyId, [selectedPersonnelId], 'Yeni görevin var', taskName);
        console.log('Tekrarlı görev için bildirim başarıyla gönderildi (eğer işlem buraya ulaştıysa)');
      } catch (notifError) {
        console.error('Tekrarlı görev için bildirim gönderilirken hata:', notifError);
      }

      onClose(); // Modal'ı kapat (eğer normal görev değilse)
    } catch (err) {
      console.error('Görev eklenirken genel hata:', err);
      setError(`Görev eklenirken bir hata oluştu: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Grup yönetimi modalını aç
  const handleOpenTaskGroupsModal = () => {
    setTaskGroupsModalOpen(true);
  };

  // Haftanın günleri
  const weekDayLabels = [
    { value: 1, label: 'Pazartesi' },
    { value: 2, label: 'Salı' },
    { value: 3, label: 'Çarşamba' },
    { value: 4, label: 'Perşembe' },
    { value: 5, label: 'Cuma' },
    { value: 6, label: 'Cumartesi' },
    { value: 0, label: 'Pazar' }
  ];

  // Resim seçme ve işleme fonksiyonları
  const handleImageSelect = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    const fileType = file.type;
    const fileSize = file.size;
    
    // Dosya tipi kontrolü (jpg, jpeg, png, webp)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(fileType)) {
      const newImages = [...taskImages];
      newImages[index] = {
        ...newImages[index],
        error: 'Sadece JPG, JPEG, PNG ve WEBP formatları desteklenir.'
      };
      setTaskImages(newImages);
      return;
    }
    
    // Dosya boyutu kontrolü (max 1MB)
    const maxSize = 1 * 1024 * 1024; // 1MB
    if (fileSize > maxSize) {
      const newImages = [...taskImages];
      newImages[index] = {
        ...newImages[index],
        error: 'Dosya boyutu 1MB\'dan büyük olamaz.'
      };
      setTaskImages(newImages);
      return;
    }
    
    // Resmi önizleme için URL oluştur
    const preview = URL.createObjectURL(file);
    
    // State'i güncelle
    const newImages = [...taskImages];
    newImages[index] = {
      file,
      preview,
      uploading: false,
      error: ''
    };
    setTaskImages(newImages);
  };
  
  // Resmi kaldır
  const handleRemoveImage = (index: number) => {
    const newImages = [...taskImages];
    
    // Eğer önizleme URL'si varsa, tarayıcı belleğinden temizle
    if (newImages[index].preview) {
      URL.revokeObjectURL(newImages[index].preview);
    }
    
    newImages[index] = { file: null, preview: '', uploading: false, error: '' };
    setTaskImages(newImages);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'white',
          py: 1.5
        }}>
          <Typography variant="h6" fontWeight="bold">{title}</Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2, py: 2 }}>
          <Collapse in={!!error}>
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => setError(null)}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              <AlertTitle>Hata</AlertTitle>
              {error}
            </Alert>
          </Collapse>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" color="primary" fontWeight="medium" gutterBottom>
              Görev Bilgileri
            </Typography>
            <Divider />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Görev Adı"
                value={taskName}
                onChange={(e) => setTaskName(e.target.value)}
                required
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <TaskIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                value={filteredPersonnel.find(person => person.id === selectedPersonnelId) || null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setSelectedPersonnelId(newValue.id);
                  } else {
                    setSelectedPersonnelId('');
                  }
                }}
                options={filteredPersonnel}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <PersonIcon fontSize="small" sx={{ mr: 1, color: 'primary.main', fontSize: '0.875rem' }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">{option.name}</Typography>
                        {option.branchName ? (
                          <Typography variant="caption" color="text.secondary">
                            {option.branchName}
                          </Typography>
                        ) : option.branchesId ? (
                          <Typography variant="caption" color="text.secondary">
                            {getBranchName(option.branchesId)}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Personel Seçin"
                    required
                size="small"
                    placeholder="Personel ara..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, state) => {
                  const inputValue = state.inputValue.toLowerCase();
                  return options.filter(option => 
                    option.name.toLowerCase().includes(inputValue) || 
                    (option.branchName && option.branchName.toLowerCase().includes(inputValue))
                  );
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                value={selectedBranch}
                onChange={(event, newValue) => {
                  if (!(isManager && branchId)) {
                    setSelectedBranch(newValue);
                  }
                }}
                options={branches}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'primary.main', fontSize: '0.875rem' }} />
                      <Typography variant="body2">{option.name}</Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={isManager && branchId ? "Şube (Şube müdürü olarak)" : "Şube Filtresi (Opsiyonel)"}
                    size="small"
                    placeholder="Şube seçin..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      readOnly: isManager && branchId ? true : false
                    }}
                    disabled={isManager && branchId ? true : false}
                  />
                )}
                disableClearable={isManager && branchId ? true : false}
                disabled={isManager && branchId ? true : false}
                sx={{
                  '& .MuiInputBase-root.Mui-disabled': {
                    backgroundColor: '#f5f5f5', // Gri arka plan
                    color: 'text.primary', // Normal metin rengi
                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)' // Safari için metin rengini düzelt
                  }
                }}
              />
              
              {/* Şube seçiliyse bilgi mesajı göster */}
              {selectedBranch && (
                <Alert 
                  severity="info" 
                  variant="outlined" 
                  sx={{ mt: 1, fontSize: '0.75rem', py: 0.5 }}
                  icon={<BusinessIcon sx={{ fontSize: '1rem' }} />}
                >
                  <Box>
                    <Typography variant="caption" display="block" fontWeight="bold">
                      "{selectedBranch.name}" şubesi seçildi
                      {isManager && branchId ? " (Şube müdürü olarak)" : ""}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Personel: {filteredPersonnel.length} kişi
                    </Typography>
                    <Typography variant="caption" display="block">
                      Görev Grupları: {filteredTaskGroups.length} adet
                      {filteredTaskGroups.length > 0 && (
                        <span>
                          &nbsp;({filteredTaskGroups.filter(g => g.branchesId).length} şubeye özel, 
                          {filteredTaskGroups.filter(g => !g.branchesId).length} genel)
                        </span>
                      )}
                    </Typography>
                  </Box>
                </Alert>
              )}
            </Grid>

            {!isManager ? (
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  value={filteredTaskGroups.find(group => group.id === selectedGroupId) || null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelectedGroupId(newValue.id);
                    } else {
                      setSelectedGroupId('');
                    }
                  }}
                  options={filteredTaskGroups}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <CategoryIcon fontSize="small" sx={{ mr: 1, color: 'primary.main', fontSize: '0.875rem' }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2">{option.name}</Typography>
                        </Box>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Görev Grubu Seçin"
                      required
                      size="small"
                      placeholder="Grup ara..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <CategoryIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {params.InputProps.endAdornment}
                            <IconButton 
                              onClick={handleOpenTaskGroupsModal} 
                              edge="end" 
                              size="small"
                              sx={{ mr: -1 }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </>
                        )
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              </Grid>
            ) : isManager && branchId ? (
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  value={filteredTaskGroups.find(group => group.id === selectedGroupId) || null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelectedGroupId(newValue.id);
                    } else {
                      setSelectedGroupId('');
                    }
                  }}
                  options={filteredTaskGroups} 
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <CategoryIcon fontSize="small" sx={{ mr: 1, color: 'primary.main', fontSize: '0.875rem' }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2">{option.name}</Typography>
                        </Box>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Şube Görev Grubu Seçin"
                      required
                      size="small"
                      placeholder="Grup ara..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <CategoryIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {params.InputProps.endAdornment}
                            <IconButton 
                              onClick={handleOpenTaskGroupsModal} 
                              edge="end" 
                              size="small"
                              sx={{ mr: -1 }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </>
                        )
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              </Grid>
            ) : null}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Görev Açıklaması"
                value={taskDescription}
                onChange={(e) => setTaskDescription(e.target.value)}
                multiline
                rows={4}
                  variant="outlined" 
                size="small"
              />
              </Grid>

            {/* Görev tipi seçenekleri - Surveys'ten açıldığında gizlenir */}
            {!isFromSurveys && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isNormalTask}
                        onChange={(e) => handleNormalTaskChange(e.target.checked)}
                        color="success"
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="subtitle2" fontWeight="medium">
                        Normal Görev
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isRecurring}
                        onChange={(e) => handleRecurringTaskChange(e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="subtitle2" fontWeight="medium">
                        Tekrarlı Görev
                      </Typography>
                    }
                  />
                </Box>
              </Grid>
            )}

            {/* Normal görev seçildiğinde başlangıç ve bitiş tarihi alanlarını göster */} 
            {isNormalTask && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Başlangıç Tarihi"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    inputProps={{
                      min: today, 
                    }}
                    InputProps={{
                      startAdornment: (
                        <TodayIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                      ),
                    }}
                    helperText="Görevin başlayacağı tarih (opsiyonel)"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Başlangıç Saati"
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                      ),
                    }}
                    helperText="Görevin başlayacağı saat (opsiyonel)"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Bitiş Tarihi"
                    type="date"
                    value={deadline}
                    onChange={(e) => setDeadline(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    inputProps={{
                      min: startDate || today, 
                    }}
                    InputProps={{
                      startAdornment: (
                        <TodayIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                      ),
                    }}
                    helperText="Görevi tamamlamak için son tarih (opsiyonel)"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Bitiş Saati"
                    type="time"
                    value={deadlineTime}
                    onChange={(e) => setDeadlineTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                      ),
                    }}
                    helperText="Görevi tamamlamak için son saat (opsiyonel)"
                  />
                </Grid>

                {/* Görev tamamlama ayarları - Basitleştirilmiş versiyon */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, mb: 0 }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Görev Tamamlama Ayarları:
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={requestCompletionPhoto}
                              onChange={(e) => {
                                console.log("Fotoğraf iste değişti:", e.target.checked);
                                setRequestCompletionPhoto(e.target.checked);
                              }}
                              size="small"
                              color="info"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <PhotoCameraIcon sx={{ mr: 0.5, color: 'info.main', fontSize: '1.1rem' }}/>
                              <Typography variant="body2">
                                Görev Bitirme Fotoğrafı İste
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={canBeRejected}
                              onChange={(e) => {
                                console.log("Görev reddedilebilsin değişti:", e.target.checked);
                                setCanBeRejected(e.target.checked);
                              }}
                              size="small"
                              color="error"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <BlockIcon sx={{ mr: 0.5, color: 'error.main', fontSize: '1.1rem' }}/>
                              <Typography variant="body2">
                                Görev Reddedilebilsin
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Resim yükleme alanı */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight="medium" gutterBottom sx={{ mt: 0 }}>
                    Görev için resim ekleyebilirsiniz (Maks: 5 adet, 1MB/resim)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {taskImages.map((image, index) => (
                      <Box 
                        key={index}
                        sx={{ 
                          width: '100px', 
                          height: '100px', 
                          border: '1px dashed #ccc',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        {image.error && (
                          <Tooltip title={image.error}>
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(255, 0, 0, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1
                              }}
                            >
                              <Typography variant="caption" color="error" align="center" sx={{ p: 1 }}>
                                Hata
                              </Typography>
                            </Box>
                          </Tooltip>
                        )}
                        
                        {image.preview ? (
                          <>
                            <img 
                              src={image.preview} 
                              alt={`Görev Resmi ${index + 1}`} 
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                            />
                            <IconButton
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                }
                              }}
                              onClick={() => handleRemoveImage(index)}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <Box
                            component="label"
                            htmlFor={`image-upload-${index}`}
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                          >
                            <input
                              id={`image-upload-${index}`}
                              type="file"
                              accept="image/jpeg,image/jpg,image/png,image/webp"
                              onChange={(e) => handleImageSelect(index, e)}
                              style={{ display: 'none' }}
                            />
                            <AddIcon sx={{ color: 'text.secondary' }} />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </>
            )}

            {isRecurring && !isFromSurveys && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                    Görev Tamamlama Türü
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={completionType === 'qr'}
                              onChange={(e) => setCompletionType(e.target.checked ? 'qr' : 'button')}
                              color="primary"
                              size="small"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <QrCodeIcon sx={{ mr: 0.5, color: 'primary.main', fontSize: '1.1rem' }}/>
                              <Typography variant="body2">
                                QR Kod ile Tamamlama
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={completionType === 'button'}
                              onChange={(e) => setCompletionType(e.target.checked ? 'button' : 'qr')}
                              color="success"
                              size="small"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <TaskIcon sx={{ mr: 0.5, color: 'success.main', fontSize: '1.1rem' }}/>
                              <Typography variant="body2">
                                Buton ile Tamamlama
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>
                    </Grid>
                    
                    {/* <Alert severity="info" sx={{ mt: 2, fontSize: '0.875rem' }}>
                      <Typography variant="caption">
                        <strong>QR Kod:</strong> Personel QR kodu okutarak görevi tamamlar. 
                        <br />
                        <strong>Buton:</strong> Personel uygulama içinde butona basarak görevi tamamlar.
                      </Typography>
                    </Alert> */}
                  </Paper>
                </Grid>

                {/* Tekrar Tipi seçenekleri - Surveys'ten açıldığında gizlenir */}
                {!isFromSurveys && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                      Tekrar Tipi
                    </Typography>
                    <Tabs
                      value={repeatType}
                      onChange={(e, newValue) => setRepeatType(newValue)}
                      variant="fullWidth"
                      sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}
                    >
                      <Tab 
                        value="daily" 
                        label="Günlük" 
                        icon={<TodayIcon fontSize="small" />} 
                        iconPosition="start"
                        sx={{ minHeight: '40px', py: 0.5 }}
                      />
                      <Tab 
                        value="weekly" 
                        label="Haftalık" 
                        icon={<WeekIcon fontSize="small" />} 
                        iconPosition="start"
                        sx={{ minHeight: '40px', py: 0.5 }}
                      />
                      <Tab 
                        value="monthly" 
                        label="Aylık" 
                        icon={<MonthIcon fontSize="small" />} 
                        iconPosition="start"
                        sx={{ minHeight: '40px', py: 0.5 }}
                      />
                      <Tab 
                        value="yearly" 
                        label="Yıllık" 
                        icon={<YearIcon fontSize="small" />} 
                        iconPosition="start"
                        sx={{ minHeight: '40px', py: 0.5 }}
                      />
                    </Tabs>
                  </Grid>
                )}

                {/* Haftalık tekrar seçenekleri - Surveys'ten açıldığında gizlenir */}
                {!isFromSurveys && repeatType === 'weekly' && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                        Haftanın Günleri
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {weeklyTaskDays.map((day, index) => (
                          <Button
                            key={day.day}
                            variant={day.selected ? "contained" : "outlined"}
                            color="primary"
                            onClick={() => handleWeeklyDayToggle(index)}
                            sx={{ minWidth: '100px', py: 0.3, px: 1, fontSize: '0.75rem' }}
                            size="small"
                          >
                            {day.dayName}
                          </Button>
                        ))}
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel>Başlama Toleransı (dk)</InputLabel>
                        <Select
                          value={startTolerance.toString()}
                          onChange={(e) => setStartTolerance(Number(e.target.value))}
                          label="Başlama Toleransı (dk)"
                        >
                          {[5, 10, 15, 20, 30, 45, 60].map((num) => (
                            <MenuItem key={num} value={num}>{num} dakika</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    {/* Seçili günler için tekrar ayarları */}
                    {weeklyTaskDays.filter(day => day.selected).map((day, dayIndex) => {
                      const actualDayIndex = weeklyTaskDays.findIndex(d => d.day === day.day);
                      return (
                        <Grid item xs={12} key={day.day} sx={{ mt: 1 }}>
                          <Paper 
                            variant="outlined" 
                            sx={{ p: 1.5, borderRadius: 1, borderColor: 'primary.light' }}
                          >
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                              {day.dayName} Tekrar Ayarları
                            </Typography>
                            
                            <Grid container spacing={1}>
                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth variant="outlined" size="small">
                                  <InputLabel>Günlük Tekrar Sayısı</InputLabel>
                                  <Select
                                    value={day.dailyRepetitions.toString()}
                                    onChange={(e) => handleWeeklyDayRepetitionChange(actualDayIndex, Number(e.target.value))}
                                    label="Günlük Tekrar Sayısı"
                                  >
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                                      <MenuItem key={num} value={num}>{num}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              
                              <Grid item xs={12}>
                                <Typography variant="body2" gutterBottom>
                                  Tekrar Saatleri:
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  {Array.from({ length: day.dailyRepetitions }).map((_, timeIndex) => (
                                    <TextField
                                      key={timeIndex}
                                      label={`${timeIndex + 1}. Tekrar Saati`}
                                      type="time"
                                      value={day.repetitionTimes[timeIndex]}
                                      onChange={(e) => handleWeeklyDayTimeChange(actualDayIndex, timeIndex, e.target.value)}
                                      InputLabelProps={{ shrink: true }}
                                      inputProps={{ step: 300 }}
                                      size="small"
                                      fullWidth
                                    />
                                  ))}
                                </Box>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </>
                )}

                {/* Günlük tekrar seçenekleri - Surveys'ten açıldığında gizlenir */}
                {!isFromSurveys && repeatType === 'daily' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel>Günlük Tekrar Sayısı</InputLabel>
                        <Select
                          value={dailyRepetitions.toString()}
                          onChange={(e) => setDailyRepetitions(Number(e.target.value))}
                          label="Günlük Tekrar Sayısı"
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                            <MenuItem key={num} value={num}>{num}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel>Başlama Toleransı (dk)</InputLabel>
                        <Select
                          value={startTolerance.toString()}
                          onChange={(e) => setStartTolerance(Number(e.target.value))}
                          label="Başlama Toleransı (dk)"
                        >
                          {[5, 10, 15, 20, 30, 45, 60].map((num) => (
                            <MenuItem key={num} value={num}>{num} dakika</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                        Tekrar Saatleri
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                        {Array.from({ length: dailyRepetitions }).map((_, index) => (
                          <TextField
                            key={index}
                            label={`${index + 1}. Tekrar Saati`}
                            type="time"
                            value={repetitionTimes[index]}
                            onChange={(e) => handleTimeChange(index, e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }}
                            size="small"
                          />
                        ))}
                      </Box>
                    </Grid>
                  </>
                )}

                {/* Aylık tekrar seçenekleri - Surveys'ten açıldığında gizlenir */}
                {!isFromSurveys && repeatType === 'monthly' && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                        Aylar
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {months.map((month) => (
                          <Button
                            key={month.value}
                            variant={selectedMonths.includes(month.value) ? "contained" : "outlined"}
                            color="primary"
                            onClick={() => handleMonthChange(month.value)}
                            sx={{ minWidth: '100px', py: 0.3, px: 1, fontSize: '0.75rem' }}
                            size="small"
                          >
                            {month.label}
                          </Button>
                        ))}
                      </Box>
                    </Grid>

                    {selectedMonths.map((monthValue) => {
                      const monthData = monthlyTaskDays.find(m => m.month === monthValue);
                      if (!monthData) return null;

                      return (
                        <Grid item xs={12} key={monthValue} sx={{ mt: 1 }}>
                          <Paper 
                            variant="outlined" 
                            sx={{ p: 1.5, borderRadius: 1, borderColor: 'primary.light' }}
                          >
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                              {monthData.monthName} Ayı Ayarları
                            </Typography>

                            <Grid container spacing={1}>
                              <Grid item xs={12}>
                                <Typography variant="body2" gutterBottom>
                                  Ayın Günleri:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                    <Button
                                      key={day}
                                      variant={monthData.days.includes(day) ? "contained" : "outlined"}
                                      color="primary"
                                      onClick={() => handleMonthDayChange(monthValue, day)}
                                      sx={{ minWidth: '40px', py: 0.3, px: 1, fontSize: '0.75rem' }}
                                      size="small"
                                    >
                                      {day}
                                    </Button>
                                  ))}
                                </Box>
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth variant="outlined" size="small">
                                  <InputLabel>Günlük Tekrar Sayısı</InputLabel>
                                  <Select
                                    value={monthData.dailyRepetitions.toString()}
                                    onChange={(e) => handleMonthDayRepetitionChange(monthValue, Number(e.target.value))}
                                    label="Günlük Tekrar Sayısı"
                                  >
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                                      <MenuItem key={num} value={num}>{num}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>

                              <Grid item xs={12}>
                                <Typography variant="body2" gutterBottom>
                                  Tekrar Saatleri:
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  {Array.from({ length: monthData.dailyRepetitions }).map((_, timeIndex) => (
                                    <TextField
                                      key={timeIndex}
                                      label={`${timeIndex + 1}. Tekrar Saati`}
                                      type="time"
                                      value={monthData.repetitionTimes[timeIndex]}
                                      onChange={(e) => handleMonthDayTimeChange(monthValue, timeIndex, e.target.value)}
                                      InputLabelProps={{ shrink: true }}
                                      inputProps={{ step: 300 }}
                                      size="small"
                                      fullWidth
                                    />
                                  ))}
                                </Box>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>
                      );
                    })}

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel>Başlama Toleransı (dk)</InputLabel>
                        <Select
                          value={startTolerance.toString()}
                          onChange={(e) => setStartTolerance(Number(e.target.value))}
                          label="Başlama Toleransı (dk)"
                        >
                          {[5, 10, 15, 20, 30, 45, 60].map((num) => (
                            <MenuItem key={num} value={num}>{num} dakika</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}

                {/* Yıllık tekrar seçenekleri - Surveys'ten açıldığında gizlenir */}
                {!isFromSurveys && repeatType === 'yearly' && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                        Yıllık Plan Tarihi
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth variant="outlined" size="small">
                            <InputLabel>Yıl</InputLabel>
                            <Select
                              value={selectedYear.toString()}
                              onChange={(e) => setSelectedYear(Number(e.target.value))}
                              label="Yıl"
                            >
                              {Array.from(
                                { length: 10 }, 
                                (_, i) => new Date().getFullYear() + i
                              ).map((year) => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <TextField
                            label="Tarih"
                            type="date"
                            value={`${selectedYear}-${yearDate}`}
                            onChange={(e) => {
                              // Tarihten sadece ay ve günü al
                              const date = new Date(e.target.value);
                              const month = (date.getMonth() + 1).toString().padStart(2, '0');
                              const day = date.getDate().toString().padStart(2, '0');
                              setYearDate(`${month}-${day}`);
                            }}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <Paper 
                        variant="outlined" 
                        sx={{ p: 2, mt: 2, borderRadius: 1, borderColor: 'primary.light' }}
                      >
                        <Typography variant="subtitle1" color="primary" fontWeight="medium" gutterBottom>
                          Yıllık Plan Görev Detayları
                        </Typography>
                        <Typography variant="caption" color="text.secondary" paragraph>
                          Bu alana yıllık görevin ayrıntılarını ve planlama detaylarını girebilirsiniz.
                        </Typography>
                        <TextField
                          label="Görev Detayları"
                          multiline
                          rows={4}
                          fullWidth
                          value={yearlyPlanDetails}
                          onChange={(e) => setYearlyPlanDetails(e.target.value)}
                          placeholder="Yıllık görev için planlama detaylarını girin..."
                          variant="outlined"
                          size="small"
                        />
                      </Paper>
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2, pt: 0 }}>
          <Button onClick={onClose} disabled={isSubmitting} size="small">
            İptal
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || !taskName.trim() || !taskDescription.trim() || !selectedPersonnelId || !selectedGroupId}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
            size="small"
          >
            {isSubmitting ? 'Ekleniyor...' : (isFromSurveys ? 'Anketi Ekle' : 'Görevi Ekle')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Görev Grupları Yönetim Modalı */}
      {companyId && (
        <TaskGroupsModal
          open={taskGroupsModalOpen}
          onClose={() => setTaskGroupsModalOpen(false)}
          companyId={companyId}
          taskGroups={taskGroups}
          isManager={isManager}
          branchId={branchId}
        />
      )}
    </>
  );
};

export default AddTaskModal; 